/**
 * Module to add functions that evaluates the Quest object
 */
import {
  ILoyaltyQuestCompletion,
  LoyaltyQuestStatus,
  LoyaltyQuestStepStatus,
} from 'generated/graphql-gateway';

import { IQuest, IQuestStep } from './types';

export const isQuestCompleted = (questStatus: LoyaltyQuestStatus | undefined) =>
  questStatus === LoyaltyQuestStatus.COMPLETED;
export const isQuestActivated = (questStatus: LoyaltyQuestStatus | undefined) =>
  questStatus === LoyaltyQuestStatus.ACTIVATED;
export const canActivateQuest = (questStatus: LoyaltyQuestStatus | undefined) =>
  questStatus === LoyaltyQuestStatus.NOT_ENROLLED;
export const isStepCompleted = (status: LoyaltyQuestStepStatus | undefined) =>
  status === LoyaltyQuestStepStatus.COMPLETED;
export const isIncentiveRedeemed = (lastCompletion: ILoyaltyQuestCompletion | undefined | null) =>
  !!lastCompletion?.redeemed;

/**
 * A tier is completed only when every step within it is completed. Shared by the
 * tier list (status badge / expansion) and the progress bar milestone crowns so
 * both agree on what "tier completed" means.
 */
export const isTierCompleted = (steps: IQuestStep[]): boolean =>
  steps.length > 0 && steps.every(step => isStepCompleted(step.status));

export type Tier = {
  bonusPoints: number;
  steps: IQuestStep[];
  status: LoyaltyQuestStepStatus;
};

export const getQuestTiers = (quest: IQuest): Tier[] => {
  const steps = quest?.steps ?? [];
  const questBonusPoints = quest?.bonusPoints ?? 0;

  const tiers: Tier[] = [];
  let currentGroupSteps: IQuestStep[] = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    currentGroupSteps.push(step);

    const hasBonusPoints = (step?.bonusPoints ?? 0) > 0;

    // if the step has bonusPoints, then we close the group
    if (hasBonusPoints) {
      tiers.push({
        bonusPoints: step.bonusPoints ?? 0,
        steps: currentGroupSteps,
        status: isTierCompleted(currentGroupSteps)
          ? LoyaltyQuestStepStatus.COMPLETED
          : LoyaltyQuestStepStatus.NOT_COMPLETED,
      });
      currentGroupSteps = [];
      continue;
    }

    const isLastStep = i === steps.length - 1;

    if (isLastStep && currentGroupSteps.length > 0) {
      tiers.push({
        bonusPoints:
          step?.bonusPoints && step.bonusPoints > 0 ? step.bonusPoints : questBonusPoints,
        steps: currentGroupSteps,
        status: isTierCompleted(currentGroupSteps)
          ? LoyaltyQuestStepStatus.COMPLETED
          : LoyaltyQuestStepStatus.NOT_COMPLETED,
      });
    }
  }

  return tiers;
};

interface IsTieredChallengeProps {
  quest: IQuest;
  tiers: Tier[];
}
export const isTieredChallenge = ({ quest, tiers }: IsTieredChallengeProps) => {
  const isRegularChallenge = quest?.steps?.every(step => (step?.bonusPoints || 0) > 0);
  const tiersContainsMoreThanOneStep = tiers.some(tier => tier.steps.length > 1);

  return !isRegularChallenge && tiers.length > 1 && tiersContainsMoreThanOneStep;
};
